import { useSessionMutation } from "../query/authenticated"
import type {
  BulkCardSyncResponse,
  BulkDeckSyncResponse,
  BulkHeroSyncResponse,
  ManagedCardRecord,
  ManagedDeckRecord,
  ManagedHeroRecord,
} from "../schemas"
import {
  bulkDeleteManagedCardRecordsFromServer,
  bulkDeleteManagedDeckRecordsFromServer,
  bulkDeleteManagedHeroRecordsFromServer,
  bulkSyncManagedCardRecordsToServer,
  bulkSyncManagedDeckRecordsToServer,
  bulkSyncManagedHeroRecordsToServer,
  deleteManagedCardRecordFromServer,
  deleteManagedDeckRecordFromServer,
  deleteManagedHeroRecordFromServer,
  fetchManagedCardRecordFromServer,
  fetchManagedDeckRecordFromServer,
  fetchManagedHeroRecordFromServer,
  syncManagedCardRecordToServer,
  syncManagedDeckRecordToServer,
  syncManagedHeroRecordToServer,
} from "./remote"

export function useSyncCardMutation() {
  return useSessionMutation({
    mutationFn: (record: ManagedCardRecord, { sessionToken }) =>
      syncManagedCardRecordToServer(record, sessionToken),
  })
}

export function useDeleteCardMutation() {
  return useSessionMutation({
    mutationFn: (cardId: string, { sessionToken }) =>
      deleteManagedCardRecordFromServer(cardId, sessionToken),
  })
}

export function usePullCardMutation() {
  return useSessionMutation({
    mutationFn: (cardId: string, { sessionToken }) =>
      fetchManagedCardRecordFromServer(cardId, sessionToken),
  })
}

export function useSyncHeroMutation() {
  return useSessionMutation({
    mutationFn: (record: ManagedHeroRecord, { sessionToken }) =>
      syncManagedHeroRecordToServer(record, sessionToken),
  })
}

export function useDeleteHeroMutation() {
  return useSessionMutation({
    mutationFn: (heroId: string, { sessionToken }) =>
      deleteManagedHeroRecordFromServer(heroId, sessionToken),
  })
}

export function usePullHeroMutation() {
  return useSessionMutation({
    mutationFn: (heroId: string, { sessionToken }) =>
      fetchManagedHeroRecordFromServer(heroId, sessionToken),
  })
}

export function useSyncDeckMutation() {
  return useSessionMutation({
    mutationFn: (record: ManagedDeckRecord, { sessionToken }) =>
      syncManagedDeckRecordToServer(record, sessionToken),
  })
}

export function useDeleteDeckMutation() {
  return useSessionMutation({
    mutationFn: (deckId: string, { sessionToken }) =>
      deleteManagedDeckRecordFromServer(deckId, sessionToken),
  })
}

export function usePullDeckMutation() {
  return useSessionMutation({
    mutationFn: (deckId: string, { sessionToken }) =>
      fetchManagedDeckRecordFromServer(deckId, sessionToken),
  })
}

export function useBulkSyncCardsMutation() {
  return useSessionMutation({
    mutationFn: (records: ManagedCardRecord[], { sessionToken }) =>
      bulkSyncManagedCardRecordsToServer(records, sessionToken),
  })
}

export function useBulkDeleteCardsMutation() {
  return useSessionMutation({
    mutationFn: (ids: string[], { sessionToken }) =>
      bulkDeleteManagedCardRecordsFromServer(ids, sessionToken),
  })
}

export function useBulkSyncHeroesMutation() {
  return useSessionMutation({
    mutationFn: (records: ManagedHeroRecord[], { sessionToken }) =>
      bulkSyncManagedHeroRecordsToServer(records, sessionToken),
  })
}

export function useBulkDeleteHeroesMutation() {
  return useSessionMutation({
    mutationFn: (ids: string[], { sessionToken }) =>
      bulkDeleteManagedHeroRecordsFromServer(ids, sessionToken),
  })
}

export function useBulkSyncDecksMutation() {
  return useSessionMutation({
    mutationFn: (records: ManagedDeckRecord[], { sessionToken }) =>
      bulkSyncManagedDeckRecordsToServer(records, sessionToken),
  })
}

export function useBulkDeleteDecksMutation() {
  return useSessionMutation({
    mutationFn: (ids: string[], { sessionToken }) =>
      bulkDeleteManagedDeckRecordsFromServer(ids, sessionToken),
  })
}

// Re-export response types for consumers
export type { BulkCardSyncResponse, BulkDeckSyncResponse, BulkHeroSyncResponse }
